import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  FileText,
  Globe,
  ShieldCheck,
  Trash2,
  CheckCircle,
  Eye,
  EyeOff,
  Settings,
  Database,
  Layers,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Save,
  RefreshCcw,
  Info,
  AlertCircle,
  Brain,
  Hash,
  Download,
  FolderTree,
  Folder,
  FolderArchive,
  Clock
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import CustomDropdown from "../components/CustomDropdown";
import KnowledgeBaseCron from "../components/KnowledgeBaseCron";

const TAB_ORDER = ["repository", "folders", "cron", "settings"];

const DocumentUpload = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("repository");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [firecrawlApiKey, setFirecrawlApiKey] = useState("");
  const [firecrawlConfigured, setFirecrawlConfigured] = useState(false);
  const [savingFirecrawl, setSavingFirecrawl] = useState(false);
  const [showFirecrawlKey, setShowFirecrawlKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // Chunks Drawer State
  const [isChunksDrawerOpen, setIsChunksDrawerOpen] = useState(false);
  const [selectedDocForChunks, setSelectedDocForChunks] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [loadingChunks, setLoadingChunks] = useState(false);

  // Zip/Folder Repository Upload State
  const [zipRepository, setZipRepository] = useState("");
  const [zipFile, setZipFile] = useState(null);
  const [uploadingZip, setUploadingZip] = useState(false);
  const zipInputRef = useRef(null);

  // Folder Hierarchy Tree State
  const [folderTree, setFolderTree] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [selectedRepository, setSelectedRepository] = useState("");
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const DOCS_PER_PAGE = 10;

  // Vector Config State
  const [configSaving, setConfigSaving] = useState(false);
  const [vectorConfig, setVectorConfig] = useState({
    strategy: 'RecursiveChunker',
    chunk_size: 2000,
    chunk_overlap: 200,
    search_top_k: 5,
    search_threshold: 0.5,
    search_type: 'hybrid',
    knowledge_source: 'vector',
    neo4j_uri: 'bolt://localhost:7687',
    neo4j_user: 'neo4j',
    neo4j_password: '',
    neo4j_database: 'neo4j',
    params: {}
  });

  const strategies = [
    {
      id: 'RecursiveChunker',
      name: 'Recursive Splitting',
      icon: Layers,
      description: 'Splits text by natural boundaries like paragraphs and periods. Recommended for most documents.',
      params: ['chunk_size', 'chunk_overlap']
    },
    {
      id: 'MarkdownChunker',
      name: 'Markdown Aware',
      icon: FileText,
      description: 'Splits based on headers (#, ##). Best for documentation and structured MD files.',
      params: ['chunk_size', 'chunk_overlap', 'split_on_headings']
    },
    {
      id: 'SemanticChunker',
      name: 'Semantic AI',
      icon: Brain,
      description: 'Uses AI to group sentences with similar meanings. Highly accurate but slower.',
      params: ['chunk_size', 'similarity_threshold', 'similarity_window']
    },
    {
      id: 'AgenticChunker',
      name: 'Agentic (LLM)',
      icon: Settings,
      description: 'Asks an LLM to find the best breakpoints. Extremely context-aware.',
      params: ['chunk_size']
    },
    {
      id: 'FixedSizeChunker',
      name: 'Fixed Size',
      icon: Hash,
      description: 'Hard cut at exact character count. Fastest but may break sentences.',
      params: ['chunk_size', 'chunk_overlap']
    }
  ];

  const documentPollTimerRef = useRef(null);

  useEffect(() => {
    if (!activeAiAgentId) return;
    loadDocuments();
    loadFirecrawlConfig();
    fetchVectorConfig();
    return () => {
      if (documentPollTimerRef.current) {
        clearTimeout(documentPollTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAiAgentId]);

  useEffect(() => {
    if (activeTab === "folders" && activeAiAgentId) {
      loadRepositories();
      loadFolderTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeAiAgentId]);

  useEffect(() => {
    if (activeTab === "folders") {
      loadFolderTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRepository]);

  // Lock body scroll when chunks drawer is open to prevent scroll-behind artifact
  useEffect(() => {
    if (isChunksDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isChunksDrawerOpen]);

  const loadDocuments = async () => {
    if (documentPollTimerRef.current) {
      clearTimeout(documentPollTimerRef.current);
    }
    try {
      const response = await ApiClient.getDocuments();
      const docs = response.documents || [];
      const formattedDocs = docs.map(doc => ({
        ...doc,
        name: doc.original_name,
        dbId: doc.id
      }));
      setDocuments(formattedDocs);
      setCurrentPage(1); // Reset to first page on load

      const hasProcessing = formattedDocs.some(doc => doc.status === "processing");
      if (hasProcessing) {
        documentPollTimerRef.current = setTimeout(loadDocuments, 5000);
      }
    } catch (error) {
      console.error("Failed to load documents:", error);
    }
  };

  const loadFirecrawlConfig = async () => {
    try {
      const response = await ApiClient.request("/firecrawl/config");
      if (response.configured) {
        setFirecrawlConfigured(true);
        setFirecrawlApiKey(response.api_key || "");
      }
    } catch (error) {
      console.error("Failed to load Firecrawl config:", error);
      setFirecrawlConfigured(false);
    }
  };

  const fetchVectorConfig = async () => {
    try {
      const response = await ApiClient.request('/api/vector/config');
      if (response) {
        setVectorConfig({
          ...response,
          params: response.params || {}
        });
      }
    } catch (error) {
      console.error('Error fetching vector config:', error);
    }
  };

  const saveVectorConfig = async (overrideConfig) => {
    setConfigSaving(true);
    try {
      const configToSave = overrideConfig && typeof overrideConfig === 'object' && !overrideConfig.nativeEvent
        ? overrideConfig
        : vectorConfig;
      await ApiClient.request('/api/vector/config', {
        method: 'POST',
        body: JSON.stringify(configToSave)
      });
      showToast("Vectorization parameters synchronized successfully!", "success");
    } catch (error) {
      console.error('Error saving vector config:', error);
      showToast("Failed to save vector parameters", "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const saveFirecrawlConfig = async () => {
    if (!firecrawlApiKey.trim()) {
      showToast("Please enter a Firecrawl API key", "warning");
      return;
    }
    setSavingFirecrawl(true);
    try {
      await ApiClient.request("/firecrawl/config", {
        method: "POST",
        body: JSON.stringify({ api_key: firecrawlApiKey }),
      });
      showToast("Firecrawl API key saved successfully!", "success");
      setFirecrawlConfigured(true);
      await loadFirecrawlConfig();
    } catch (error) {
      console.error("Failed to save Firecrawl config:", error);
      showToast(`Failed to save Firecrawl API key: ${error.message}`, "error");
    } finally {
      setSavingFirecrawl(false);
    }
  };

  const loadRepositories = async () => {
    try {
      const response = await ApiClient.getRepositories();
      setRepositories(response.repositories || []);
    } catch (error) {
      console.error("Failed to load repositories:", error);
    }
  };

  const loadFolderTree = async () => {
    setLoadingFolders(true);
    try {
      const response = await ApiClient.getFolderDocuments(selectedRepository || null);
      setFolderTree(response.folders || []);
    } catch (error) {
      console.error("Failed to load folder hierarchy:", error);
      showToast("Failed to load folder hierarchy", "error");
    } finally {
      setLoadingFolders(false);
    }
  };

  const toggleFolder = (folderName) => {
    setExpandedFolders((prev) => ({ ...prev, [folderName]: !prev[folderName] }));
  };

  const handleZipUpload = async () => {
    if (!zipRepository.trim()) {
      showToast("Please enter a repository name", "warning");
      return;
    }
    if (!zipFile) {
      showToast("Please select a .zip file", "warning");
      return;
    }
    setUploadingZip(true);
    try {
      const response = await ApiClient.uploadZipRepository(zipRepository.trim(), zipFile);
      showToast(response.message || `Repository '${zipRepository}' uploaded successfully.`, "success");
      setZipFile(null);
      if (zipInputRef.current) zipInputRef.current.value = "";
      (response.files || []).forEach((file) => pollDocumentStatus(file.id));
      await loadDocuments();
    } catch (error) {
      console.error("Zip upload failed:", error);
      showToast(error.message || "Zip upload failed. Please try again.", "error");
    } finally {
      setUploadingZip(false);
    }
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const response = await ApiClient.uploadDocuments(files, {});
      const uploadedFiles = response.files || [];
      const processingDocs = uploadedFiles.map((file) => ({
        name: file.filename,
        chunks: "Processing...",
        status: "processing",
        dbId: file.id,
      }));
      setDocuments((prev) => [...prev, ...processingDocs]);
      showToast(`Uploaded ${files.length} document(s).`, "success");
      uploadedFiles.forEach((file) => {
        pollDocumentStatus(file.id);
      });
    } catch (error) {
      console.error("Upload failed:", error);
      showToast("Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pollDocumentStatus = async (docId) => {
    const checkStatus = async () => {
      try {
        const statusResponse = await ApiClient.getDocumentStatus(docId);
        if (statusResponse.status === "completed") {
          await loadDocuments();
        } else if (statusResponse.status === "failed") {
          await loadDocuments();
          const detail = statusResponse.status_detail || "";
          if (detail.includes("No embedder configured")) {
            showToast("⚠️ No embedder configured. Please configure an embedder.", "error", 8000);
          } else {
            showToast(`❌ Vectorization failed: ${detail || "Embedding error. Check provider credits and key."}`, "error", 10000);
          }
        } else {
          setTimeout(checkStatus, 5000);
        }
      } catch (error) {
        console.error("Status polling failed:", error);
        setTimeout(checkStatus, 1000);
      }
    };
    setTimeout(checkStatus, 100);
  };

  const removeDocument = (docId) => {
    if (!docId) {
      showToast("Cannot delete document: Invalid document ID", "error");
      return;
    }
    setDocToDelete(docId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.deleteDocument(docToDelete);
      await loadDocuments();
      showToast("Document deleted successfully", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      showToast("Delete failed. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      await ApiClient.downloadDocument(docId, filename);
      showToast("Download started successfully!", "success");
    } catch (error) {
      console.error("Download failed:", error);
      showToast("Download failed. Please try again.", "error");
    }
  };

  const handleViewChunks = async (doc) => {
    setIsChunksDrawerOpen(true);
    setLoadingChunks(true);
    setSelectedDocForChunks(null);
    try {
      const response = await ApiClient.getDocumentChunks(doc.dbId);
      if (response && response.document) {
        setSelectedDocForChunks({
          ...response.document,
          name: response.document.original_name,
          dbId: response.document.id
        });
      }
      setChunks(response.chunks || []);
    } catch (error) {
      console.error("Failed to load chunks:", error);
      showToast("Failed to retrieve document chunks", "error");
      setIsChunksDrawerOpen(false);
    } finally {
      setLoadingChunks(false);
    }
  };

  const handleWebsiteScrape = async () => {
    if (!websiteUrl.trim()) {
      showToast("Please enter a website URL", "warning");
      return;
    }
    setScraping(true);
    try {
      const response = await ApiClient.request("/documents/add-website", {
        method: "POST",
        body: JSON.stringify({
          url: websiteUrl,
          max_depth: 2,
          max_links: 10,
        }),
      });
      if (response.success !== false) {
        showToast(response.message || "Website content added successfully!", "success");
        setTimeout(() => {
          setWebsiteUrl("");
          setScraping(false);
          loadDocuments();
        }, 2000);
      } else {
        setScraping(false);
        throw new Error(response.message || "Failed to add website content");
      }
    } catch (error) {
      setScraping(false);
      console.error("Website scraping failed:", error);
      showToast("Failed to add website content", "error");
    }
  };

  const currentStrategy = strategies.find(s => s.id === vectorConfig.strategy) || strategies[0];

  return (
    <>
      <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Knowledge Base
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Infuse your agent with specialized knowledge. Upload, manage, and configure your proprietary datasets.
            </p>
          </div>
          <div className="self-start sm:self-auto flex flex-col xl:flex-row items-start xl:items-center gap-4">
            {/* Minimal Tab Switcher */}
            <div className="flex p-1 bg-zinc-100/80 rounded-xl border border-zinc-200/60 w-full sm:w-auto overflow-x-auto custom-scrollbar">
              <button
                onClick={() => setActiveTab("repository")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "repository"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Database size={13} />
                <span>Repository</span>
              </button>
              <button
                onClick={() => setActiveTab("folders")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "folders"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <FolderTree size={13} />
                <span>Folders</span>
              </button>
              <button
                onClick={() => setActiveTab("cron")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "cron"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Clock size={13} />
                <span>Cron</span>
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "settings"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Settings size={13} />
                <span>Settings</span>
              </button>
            </div>

            <div className="flex items-center gap-2.5 h-auto sm:h-[38px]">
              <span className={`text-xs font-medium ${vectorConfig.knowledge_source !== 'graph' ? "text-zinc-900 font-semibold" : "text-zinc-400"}`}>
                {vectorConfig.knowledge_source !== 'graph' ? "Active" : "Inactive"}
              </span>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={vectorConfig.knowledge_source !== 'graph'}
                  onChange={async () => {
                    const newSource = vectorConfig.knowledge_source === 'graph' ? 'vector' : 'graph';
                    const updated = { ...vectorConfig, knowledge_source: newSource };
                    setVectorConfig(updated);

                    setConfigSaving(true);
                    try {
                      await ApiClient.request('/api/vector/config', {
                        method: 'POST',
                        body: JSON.stringify(updated)
                      });
                      showToast(`Knowledge source updated to ${newSource === 'graph' ? 'Knowledge Graph' : 'Vector Search'}!`, "success");
                    } catch (e) {
                      showToast("Failed to update knowledge source", "error");
                    } finally {
                      setConfigSaving(false);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
              </label>
            </div>
          </div>
        </div>


        {/* Sliding Tab Track */}
        <div className="overflow-hidden w-full pt-2">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / 4}%)`,
              width: "400%",
            }}
          >
            {/* Panel 1: Repository */}
            <div className="w-1/4 shrink-0 sm:px-2">
              <div className="grid gap-8">
            {/* Upload Area */}
            <div
              className={`bg-white rounded-2xl p-6 sm:p-8 border-2 border-dashed transition-all duration-300 relative overflow-hidden group ${isDragging
                ? "border-zinc-900 bg-zinc-50 ring-4 ring-zinc-900/5"
                : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50/30"
                } cursor-pointer shadow-xs`}
              onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(Array.from(e.dataTransfer.files)); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => handleFiles(Array.from(e.target.files))}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
              />
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <Upload className="text-zinc-400 shrink-0" />
                <h3 className="text-sm sm:text-base font-semibold text-zinc-900 mb-1 tracking-tight">
                  {isDragging ? "Drop Files to Upload" : "Document Upload"}
                </h3>
                <p className="text-zinc-500 text-xs mb-1 max-w-xs leading-relaxed">
                  Drag and drop files or click to browse
                </p>
                <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                  {['PDF', 'DOCX', 'TXT', 'CSV', 'XLSX'].map(ext => (
                    <span key={ext} className="px-2.5 py-0.5 bg-zinc-100 text-zinc-600 rounded-md text-[11px] font-medium">{ext}</span>
                  ))}
                </div>

                {uploading && (
                  <div className="mt-4 flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg shadow-xs text-xs font-medium">
                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Processing Documents...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Zip Repository Upload */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-zinc-200/80 shadow-xs">
              <div className="flex items-center gap-3 mb-5">
                <FolderArchive className="text-zinc-700 shrink-0" size={18} strokeWidth={2} />
                <div>
                  <h3 className="font-semibold text-zinc-900 text-xs tracking-tight">Repository Upload (.zip)</h3>
                  <p className="text-[11px] text-zinc-500 font-normal">Folder-structure-aware ingestion</p>
                </div>
              </div>
              <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-stretch">
                <input
                  type="text"
                  placeholder="Repository name (e.g. xshield-doc)"
                  value={zipRepository}
                  onChange={(e) => setZipRepository(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 text-xs transition-all"
                />
                <button
                  type="button"
                  onClick={() => zipInputRef.current?.click()}
                  className="w-full px-3.5 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-left text-xs font-medium text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 transition-all truncate"
                >
                  {zipFile ? zipFile.name : "Choose .zip file..."}
                </button>
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files[0] || null)}
                  className="hidden"
                />
                <button
                  onClick={handleZipUpload}
                  disabled={uploadingZip || !zipRepository.trim() || !zipFile}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-semibold shadow-xs whitespace-nowrap"
                >
                  {uploadingZip ? "Extracting..." : "Upload Repository"}
                </button>
              </div>
              <p className="text-xs text-zinc-400 mt-3">
                Preserves folder hierarchy &middot; tags chunks with repository/folder metadata &middot; view structure in the Folders tab
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* Firecrawl Configuration */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 border border-zinc-200/80 shadow-xs">
                <div className="flex items-center gap-3 pb-4 mb-5 border-b border-zinc-100">
                  <ShieldCheck className="text-zinc-700 shrink-0" size={18} />
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">Crawler Auth</h3>
                    <p className="text-xs text-zinc-400">Secure parsing engine</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type={showFirecrawlKey ? "text" : "password"}
                      placeholder="Firecrawl API Key"
                      value={firecrawlApiKey}
                      onChange={(e) => setFirecrawlApiKey(e.target.value)}
                      className="w-full px-3.5 py-2.5 pr-10 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 font-mono text-xs transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFirecrawlKey(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showFirecrawlKey ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  <button
                    onClick={saveFirecrawlConfig}
                    disabled={savingFirecrawl || !firecrawlApiKey.trim()}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-semibold shadow-xs"
                  >
                    {savingFirecrawl ? "Saving..." : "Connect Web Crawler"}
                  </button>
                </div>
              </div>

              {/* Website Scraping */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 border border-zinc-200/80 shadow-xs">
                <div className="flex items-center gap-3 pb-4 mb-5 border-b border-zinc-100">
                  <Globe className="text-zinc-700 shrink-0" size={18} />
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">URL Ingestion</h3>
                    <p className="text-xs text-zinc-400">Live web synchronization</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <input
                    type="url"
                    placeholder="https://docs.example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    disabled={!firecrawlConfigured || scraping}
                    className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 text-xs transition-all disabled:opacity-50"
                  />
                  <button
                    onClick={handleWebsiteScrape}
                    disabled={scraping || !websiteUrl.trim() || !firecrawlConfigured}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-semibold shadow-xs"
                  >
                    {scraping ? "Crawl Active..." : "Start Web Crawl"}
                  </button>
                </div>
              </div>
            </div>



            {/* List */}
            {documents.length > 0 && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 border border-zinc-200/80 shadow-xs">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-semibold text-zinc-900">Uploaded Documents</h3>
                  <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-50 border border-zinc-200 px-2.5 py-1 rounded">
                    {documents.length} vectors total
                  </span>
                </div>
                <div className="grid gap-2 mb-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                  {documents.slice((currentPage - 1) * DOCS_PER_PAGE, currentPage * DOCS_PER_PAGE).map((doc, index) => (
                    <div
                      key={doc.id || index}
                      className="flex items-center justify-between p-2.5 bg-zinc-50/50 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all duration-300 group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="text-zinc-700 shrink-0" size={18} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-zinc-900 truncate pr-4">
                            {doc.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {doc.status === "processing" ? (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-zinc-500 bg-zinc-50 border border-zinc-200 px-1.5 py-0.5 rounded">
                                <RefreshCcw size={8} className="animate-spin" /> Processing
                              </span>
                            ) : doc.status === "failed" ? (
                              <span className="text-[10px] font-medium text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded">
                                Error: {doc.status_detail || "Processing Failed"}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                                <CheckCircle size={8} /> {doc.chunks || 0} chunks
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewChunks(doc)}
                          className="p-2 text-zinc-300 hover:text-zinc-900 hover:bg-zinc-50 rounded-lg transition-all"
                          title="View chunks"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(doc.dbId, doc.name)}
                          className="p-2 text-zinc-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                          title="Download document"
                        >
                          <Download size={15} />
                        </button>
                        <button
                          onClick={() => removeDocument(doc.dbId)}
                          className="p-2 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete document"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {Math.ceil(documents.length / DOCS_PER_PAGE) > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                    <span className="text-xs text-zinc-400 font-medium">
                      Page {currentPage} of {Math.ceil(documents.length / DOCS_PER_PAGE)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(documents.length / DOCS_PER_PAGE)))}
                        disabled={currentPage === Math.ceil(documents.length / DOCS_PER_PAGE)}
                        className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
              </div>
            </div>

            {/* Panel 2: Folders */}
            <div className="w-1/4 shrink-0 sm:px-2 border-l border-zinc-200/50">
              <div className="grid gap-8">
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-zinc-200/80 shadow-xs">
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-zinc-100 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <FolderTree className="text-zinc-700 shrink-0" size={18} strokeWidth={2} />
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">Repository Hierarchy</h3>
                    <p className="text-xs text-zinc-400">Folder-aware knowledge structure</p>
                  </div>
                </div>
                <CustomDropdown
                  options={[
                    { id: "", name: "All Repositories" },
                    ...repositories.map((repo) => ({ id: repo, name: repo }))
                  ]}
                  value={selectedRepository}
                  onChange={(val) => setSelectedRepository(val)}
                  className="w-full md:w-52"
                  buttonClassName="px-3.5 py-2 border border-zinc-200 rounded-xl hover:border-zinc-900 text-xs"
                />
              </div>

              {loadingFolders ? (
                <div className="flex items-center justify-center py-12 text-zinc-400">
                  <RefreshCcw size={20} className="animate-spin" />
                </div>
              ) : folderTree.length === 0 ? (
                <div className="text-center py-12">
                  <Folder size={28} className="text-zinc-200 mx-auto mb-3" />
                  <p className="text-xs font-medium text-zinc-400">No folder-uploaded documents yet</p>
                  <p className="text-xs text-zinc-300 mt-1">Upload a repository (.zip) from the Repository tab to see its structure here.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {folderTree.map((folder) => (
                    <div key={folder.name} className="border border-zinc-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggleFolder(folder.name)}
                        className="w-full flex items-center justify-between p-3 bg-zinc-50/50 hover:bg-zinc-50 transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          {expandedFolders[folder.name] ? (
                            <ChevronDown size={13} className="text-zinc-400" />
                          ) : (
                            <ChevronRight size={13} className="text-zinc-400" />
                          )}
                          <Folder size={14} className="text-zinc-400" />
                          <span className="text-xs font-semibold text-zinc-900">{folder.name || "(root)"}</span>
                        </div>
                        <span className="text-[10px] font-medium text-zinc-400 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">
                          {folder.files.length} file{folder.files.length !== 1 ? "s" : ""}
                        </span>
                      </button>
                      {expandedFolders[folder.name] && (
                        <div className="p-2 space-y-1.5 bg-white">
                          {folder.files.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 pl-8 bg-zinc-50/30 rounded-lg">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText size={13} className="text-zinc-300 shrink-0" />
                                <span className="text-xs font-medium text-zinc-700 truncate">{file.name}</span>
                              </div>
                              {file.status === "processing" ? (
                                <span className="flex items-center gap-1 text-[10px] font-medium text-zinc-500 bg-zinc-50 border border-zinc-200 px-1.5 py-0.5 rounded shrink-0">
                                  <RefreshCcw size={8} className="animate-spin" /> Processing
                                </span>
                              ) : file.status === "failed" ? (
                                <span
                                  className="text-[10px] font-medium text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded shrink-0"
                                  title={file.status_detail}
                                >
                                  Error
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded shrink-0">
                                  <CheckCircle size={8} /> {file.chunks || 0} chunks
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
              </div>
            </div>

            {/* Panel 3: Cron */}
            <div className="w-1/4 shrink-0 sm:px-2 border-l border-zinc-200/50">
              <KnowledgeBaseCron
                activeAiAgentId={activeAiAgentId}
                onSyncSuccess={() => {
                  loadDocuments();
                  loadRepositories();
                }}
              />
            </div>

            {/* Panel 4: Settings */}
            <div className="w-1/4 shrink-0 sm:px-2 border-l border-zinc-200/50">
              <div className="space-y-8">
            {/* 1. Chunking Strategy Card */}
            <div className="bg-white border border-zinc-200/80 rounded-2xl shadow-xs overflow-hidden relative">
              {/* Strategy Switcher (Inside Card Top) */}
              <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Layers className="text-zinc-700 shrink-0" size={18} strokeWidth={2} />
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900">Chunking Strategy</h3>
                      <p className="text-xs text-zinc-400">Select document chunking method</p>
                    </div>
                  </div>

                  {/* Custom Dropdown + Info Tooltip */}
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <CustomDropdown
                      options={strategies}
                      value={vectorConfig.strategy}
                      onChange={(val) => setVectorConfig({ ...vectorConfig, strategy: val })}
                      className="w-full md:w-72"
                      buttonClassName="px-3.5 py-2.5 border border-zinc-200 rounded-xl hover:border-zinc-900 text-xs"
                      renderSelected={(selected) => {
                        const SelectedIcon = selected?.icon || Layers;
                        return (
                          <div className="flex items-center gap-2">
                            <SelectedIcon size={14} className="text-zinc-900" />
                            <span className="text-xs font-semibold text-zinc-900">{selected?.name}</span>
                          </div>
                        );
                      }}
                      renderOption={(s, isSelected) => (
                        <div className="flex items-center gap-3 w-full">
                          <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-400'}`}>
                            <s.icon size={14} />
                          </div>
                          <div>
                            <p className={`text-xs font-semibold ${isSelected ? 'text-zinc-900' : 'text-zinc-600'}`}>
                              {s.name}
                            </p>
                            <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">{s.description}</p>
                          </div>
                        </div>
                      )}
                    />

                    {/* Tooltip Wrapper */}
                    <div className="relative group flex items-center justify-center shrink-0">
                      <Info className="text-zinc-700 shrink-0" size={18} />
                      {/* Tooltip Box */}
                      <div className="absolute right-[-8px] bottom-full mb-3 w-[230px] p-3 bg-zinc-900 text-white text-xs leading-relaxed rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 shadow-lg pointer-events-none">
                        <div className="relative z-10">
                          Chunking strategies are immutable for existing documents (new uploads will inherit them). Retrieval and reranking parameters take effect instantly.
                        </div>
                        {/* Tooltip Arrow */}
                        <div className="absolute top-full right-[18px] -mt-1 w-3 h-3 bg-zinc-900 rotate-45 z-0" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Params Area */}
              <div className="p-6 sm:p-8 space-y-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-zinc-900">{currentStrategy.name}</h4>
                    <p className="text-xs text-zinc-400 mt-0.5 max-w-md">{currentStrategy.description}</p>
                  </div>
                  <button
                    onClick={saveVectorConfig}
                    disabled={configSaving}
                    className="inline-flex items-center gap-2 px-4 py-2.5 sm:py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors shadow-xs active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {configSaving ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {configSaving ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-6 pt-2">
                  {/* Chunk Size */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                        Chunk Size <Info size={13} className="text-zinc-300" />
                      </label>
                      <span className="px-3 py-1 bg-zinc-900 text-white text-[10px] font-semibold rounded-full">
                        {vectorConfig.chunk_size} chars
                      </span>
                    </div>
                    <input
                      type="range" min="200" max="8000" step="100"
                      value={vectorConfig.chunk_size}
                      onChange={(e) => setVectorConfig({ ...vectorConfig, chunk_size: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                    />
                  </div>

                  {/* Overlap / Threshold Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentStrategy.params.includes('chunk_overlap') && (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <label className="text-xs font-medium text-zinc-600">Chunk Overlap</label>
                          <span className="px-3 py-1 bg-zinc-50 text-zinc-900 text-[10px] font-semibold rounded-full border border-zinc-200">
                            {vectorConfig.chunk_overlap} chars
                          </span>
                        </div>
                        <input
                          type="range" min="0" max="1000" step="50"
                          value={vectorConfig.chunk_overlap}
                          onChange={(e) => setVectorConfig({ ...vectorConfig, chunk_overlap: parseInt(e.target.value) })}
                          className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                        />
                      </div>
                    )}

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <label className="text-xs font-medium text-zinc-600">
                          {vectorConfig.params?.reranker_type && vectorConfig.params?.reranker_type !== "none"
                            ? "Retrieval Pool (K)"
                            : "Retrieval Context (K)"}
                        </label>
                        <span className="px-3 py-1 bg-zinc-50 text-zinc-900 text-[10px] font-semibold rounded-full border border-zinc-200">
                          {vectorConfig.search_top_k} results
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max={vectorConfig.params?.reranker_type && vectorConfig.params?.reranker_type !== "none" ? "50" : "20"}
                        step="1"
                        value={vectorConfig.search_top_k}
                        onChange={(e) => setVectorConfig({ ...vectorConfig, search_top_k: parseInt(e.target.value) })}
                        className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Search Type
                      </label>
                      <CustomDropdown
                        options={[
                          { value: "hybrid", label: "Hybrid Search" },
                          { value: "vector", label: "Vector Search" },
                          { value: "keyword", label: "Keyword Search" }
                        ]}
                        value={vectorConfig.search_type || "hybrid"}
                        onChange={(val) => setVectorConfig({ ...vectorConfig, search_type: val })}
                        buttonClassName="w-full border border-zinc-200 rounded-xl hover:border-zinc-900 px-3.5 py-2.5 text-xs text-left"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Multimodal RAG Card */}
            <div className="bg-white border border-zinc-200/80 rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Eye className="text-zinc-700 shrink-0" size={18} strokeWidth={2} />
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">Multimodal RAG</h3>
                    <p className="text-xs text-zinc-400">Image processing & vision descriptions for PDF documents</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    const isEnabled = vectorConfig.params?.multimodal_rag || false;
                    const updatedConfig = {
                      ...vectorConfig,
                      params: {
                        ...vectorConfig.params,
                        multimodal_rag: !isEnabled
                      }
                    };
                    setVectorConfig(updatedConfig);
                    await saveVectorConfig(updatedConfig);
                  }}
                  className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out cursor-pointer ${
                    vectorConfig.params?.multimodal_rag ? "bg-zinc-900" : "bg-zinc-200"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
                      vectorConfig.params?.multimodal_rag ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              <p className="text-xs text-zinc-500 leading-relaxed">
                Enabling this will extract images, call the configured Vision Model to describe them, and inline URLs/descriptions for vectorization.
              </p>
            </div>

            {/* 3. Reranking Engine Card */}
            <div className="bg-white border border-zinc-200/80 rounded-2xl p-5 sm:p-6 shadow-xs space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
                <RefreshCcw className="text-zinc-700 shrink-0" size={18} strokeWidth={2} />
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900">Reranking Engine</h3>
                  <p className="text-xs text-zinc-400">Optimize search relevance using a semantic reranker model</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-end">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-600 block">Select Reranker</label>
                  <CustomDropdown
                    options={[
                      { value: "none", label: "None (Standard Search)" },
                      { value: "cohere", label: "Cohere Rerank" },
                      { value: "aws_bedrock", label: "AWS Bedrock Rerank" }
                    ]}
                    value={vectorConfig.params?.reranker_type || "none"}
                    onChange={(val) => setVectorConfig({
                      ...vectorConfig,
                      params: { ...vectorConfig.params, reranker_type: val }
                    })}
                    buttonClassName="border border-zinc-200 rounded-xl hover:border-zinc-900"
                  />
                </div>

                {(vectorConfig.params?.reranker_type === "cohere") && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-600 block">Cohere Model</label>
                    <CustomDropdown
                      options={[
                        { value: "rerank-multilingual-v3.0", label: "Rerank Multilingual v3.0" },
                        { value: "rerank-english-v3.0", label: "Rerank English v3.0" },
                        { value: "rerank-multilingual-v2.0", label: "Rerank Multilingual v2.0" },
                        { value: "rerank-english-v2.0", label: "Rerank English v2.0" }
                      ]}
                      value={vectorConfig.params?.reranker_model || "rerank-multilingual-v3.0"}
                      onChange={(val) => setVectorConfig({
                        ...vectorConfig,
                        params: { ...vectorConfig.params, reranker_model: val }
                      })}
                      buttonClassName="border border-zinc-200 rounded-xl hover:border-zinc-900"
                    />
                  </div>
                )}

                {(vectorConfig.params?.reranker_type === "cohere") && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-600 block">Cohere API Key</label>
                    <input
                      type="password"
                      placeholder="Enter Cohere API Key"
                      value={vectorConfig.params?.reranker_api_key || ""}
                      onChange={(e) => {
                        setVectorConfig({
                          ...vectorConfig,
                          params: {
                            ...vectorConfig.params,
                            reranker_api_key: e.target.value
                          }
                        });
                      }}
                      className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 text-xs"
                    />
                  </div>
                )}

                {(vectorConfig.params?.reranker_type === "aws_bedrock") && (
                  <>
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">AWS Bedrock Model</label>
                      <CustomDropdown
                        options={[
                          { value: "cohere.rerank-v3-5:0", label: "Cohere Rerank 3.5" },
                          { value: "amazon.rerank-v1:0", label: "Amazon Rerank 1.0" }
                        ]}
                        value={vectorConfig.params?.aws_bedrock_model || "cohere.rerank-v3-5:0"}
                        onChange={(val) => setVectorConfig({
                          ...vectorConfig,
                          params: { ...vectorConfig.params, aws_bedrock_model: val }
                        })}
                        buttonClassName="border border-zinc-200 rounded-xl hover:border-zinc-900"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">AWS Access Key ID</label>
                      <input
                        type="text"
                        placeholder="Enter AWS Access Key ID"
                        value={vectorConfig.params?.aws_access_key_id || ""}
                        onChange={(e) => {
                          setVectorConfig({
                            ...vectorConfig,
                            params: {
                              ...vectorConfig.params,
                              aws_access_key_id: e.target.value
                            }
                          });
                        }}
                        className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 text-xs"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">AWS Secret Access Key</label>
                      <input
                        type="password"
                        placeholder="Enter AWS Secret Access Key"
                        value={vectorConfig.params?.aws_secret_access_key || ""}
                        onChange={(e) => {
                          setVectorConfig({
                            ...vectorConfig,
                            params: {
                              ...vectorConfig.params,
                              aws_secret_access_key: e.target.value
                            }
                          });
                        }}
                        className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 text-xs"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">AWS Region</label>
                      <input
                        type="text"
                        placeholder="Enter AWS region"
                        value={vectorConfig.params?.aws_region || ""}
                        onChange={(e) => {
                          setVectorConfig({
                            ...vectorConfig,
                            params: {
                              ...vectorConfig.params,
                              aws_region: e.target.value
                            }
                          });
                        }}
                        className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 text-zinc-900 text-xs"
                      />
                    </div>
                  </>
                )}

                {vectorConfig.params?.reranker_type && vectorConfig.params?.reranker_type !== "none" && (
                  <div className="space-y-3 w-full">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-medium text-zinc-600 block">Reranker Top-N Chunks</label>
                      <span className="px-3 py-1 bg-zinc-50 text-zinc-900 text-[10px] font-semibold rounded-full border border-zinc-200">
                        {vectorConfig.params?.reranker_top_n || 5} chunks
                      </span>
                    </div>
                    <input
                      type="range" min="1" max="20" step="1"
                      value={vectorConfig.params?.reranker_top_n || 5}
                      onChange={(e) => {
                        setVectorConfig({
                          ...vectorConfig,
                          params: {
                            ...vectorConfig.params,
                            reranker_top_n: parseInt(e.target.value)
                          }
                        });
                      }}
                      className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Document?"
        message="This action will permanently delete this document and its chunk vectors from storage."
      />

      {/* Chunks Side Drawer */}
      {isChunksDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsChunksDrawerOpen(false)}
          />
          {/* Drawer Panel */}
          <div className="relative w-full max-w-3xl bg-white h-full shadow-2xl flex flex-col z-10 transition-transform duration-300 transform translate-x-0 border-l border-zinc-100">
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
              <div className="flex items-center gap-3">
                <Database className="text-zinc-700 shrink-0" size={18} />
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900 truncate max-w-md">
                    {selectedDocForChunks ? selectedDocForChunks.name : "Loading document..."}
                  </h3>
                  <p className="text-xs text-zinc-400">
                    Knowledge Base Chunk Viewer
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsChunksDrawerOpen(false)}
                className="px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 text-xs font-semibold rounded-lg transition-all"
              >
                Close
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[220px_1fr] divide-y md:divide-y-0 md:divide-x divide-zinc-100">
              {/* Left Pane - Metadata */}
              <div className="p-5 bg-zinc-50/30 overflow-y-auto space-y-5">
                <div>
                  <span className="text-[10px] font-medium text-zinc-400 block mb-1">Document ID</span>
                  <span className="text-[10px] font-mono text-zinc-700 break-all select-all bg-zinc-100 px-2 py-1 rounded-md block">
                    {selectedDocForChunks ? selectedDocForChunks.document_id : "Loading..."}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] font-medium text-zinc-400 block mb-1">Created At</span>
                  <span className="text-xs font-semibold text-zinc-900">
                    {(() => {
                      if (!selectedDocForChunks) return "Loading...";
                      const dt = selectedDocForChunks.created_at;
                      if (!dt) return "Unknown";
                      let clean = typeof dt === 'string' ? dt.replace(' ', 'T') : dt;
                      if (typeof clean === 'string' && !clean.endsWith('Z') && !clean.includes('+')) {
                        clean += 'Z';
                      }
                      const dateObj = new Date(clean);
                      return isNaN(dateObj.getTime()) ? String(dt) : dateObj.toLocaleString();
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] font-medium text-zinc-400 block mb-1">File Size</span>
                  <span className="text-xs font-semibold text-zinc-900">
                    {selectedDocForChunks ? (selectedDocForChunks.file_size ? `${(selectedDocForChunks.file_size / 1024).toFixed(1)} KB` : 'Unknown') : 'Loading...'}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] font-medium text-zinc-400 block mb-1">Repository</span>
                  <span className="text-xs font-semibold text-zinc-900">
                    {selectedDocForChunks ? (selectedDocForChunks.repository || "Global") : 'Loading...'}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] font-medium text-zinc-400 block mb-1">RAG Index Type</span>
                  <span className="text-xs font-semibold text-zinc-900">
                    PgVector (Hybrid)
                  </span>
                </div>
                <div>
                  <span className="text-[10px] font-medium text-zinc-400 block mb-1">Total Chunks</span>
                  <span className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-600 border border-blue-100 rounded text-[10px] font-semibold">
                    {loadingChunks ? "Calculating..." : `${chunks.length} chunks`}
                  </span>
                </div>
              </div>

              {/* Right Pane - Chunks Content */}
              <div className="flex-1 p-5 overflow-y-auto space-y-4">
                <h4 className="text-xs font-semibold text-zinc-400">File Content Slices</h4>
                {loadingChunks ? (
                  <div className="flex flex-col items-center justify-center h-64 text-zinc-400">
                    <RefreshCcw size={22} className="animate-spin mb-3" />
                    <span className="text-xs font-medium">Loading document chunks...</span>
                  </div>
                ) : chunks.length === 0 ? (
                  <div className="text-center py-20">
                    <AlertCircle className="w-8 h-8 text-zinc-200 mx-auto mb-3" />
                    <p className="text-xs font-medium text-zinc-400">No vectorized chunks found</p>
                    <p className="text-xs text-zinc-300 mt-1">Check if the document status is fully completed.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chunks.map((chunk, index) => (
                      <div key={chunk.id || index} className="bg-white border border-zinc-100 rounded-xl p-4 shadow-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="bg-zinc-100 text-zinc-500 border border-zinc-200 px-2 py-0.5 rounded text-[10px] font-semibold">
                            Chunk #{index + 1}
                          </span>
                          {chunk.meta_data?.page && (
                            <span className="text-[10px] font-medium text-zinc-400">
                              Page {chunk.meta_data.page}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-700 leading-relaxed font-mono whitespace-pre-wrap select-text">
                          {chunk.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentUpload;
